<template>
	<div style="padding: 10px">
		<v-progress-linear indeterminate color="accent" v-show="loading"></v-progress-linear>
		<div v-if="offer !== undefined">
			<h1>Angebot {{ offer.calculationId }}</h1>
			<div>Spätester Liefertermin: {{ offer.latestDeliveryDate | formatDate }}</div>

			<v-btn
				class="blexon-button"
				title="Angebot abschliessen und an Kunde senden"
				:disabled="loading || offer.state !== 0"
				@click="showSubmitDialog = true"
			>
				Abschliessen
			</v-btn>
			<v-expansion-panel>
				<v-expansion-panel-content
					v-for="material of getMaterialGroups()"
					:key="material.blexonId"
				>
					<template v-slot:header>
						<h2>{{ getMaterialnameOfFertiger(material[0]) }}</h2>
					</template>
					<div>
						<price-text-box
							label="Materialpreis"
							style="margin-left: 1em"
							:readonly="offer.state !== 0"
							:value="getMaterialPrice(material)"
							@valueChanged="updateMaterialPrice($event, material[0])"
						>
						</price-text-box>
					</div>
					<offer-position-component
						v-for="position of material[1]"
						:key="position.articleId"
						:offerState="offer.state"
						:position="position"
						:worksteps="worksteps.filter((w) => w.articleId === position.articleId)"
						@updateWorksteps="loadWorksteps"
					>
					</offer-position-component>
				</v-expansion-panel-content>
			</v-expansion-panel>
		</div>
		<v-dialog v-if="showSubmitDialog" v-model="showSubmitDialog" width="500">
			<v-card>
				<v-card-title>
					<h1>Angebot an Kunde senden</h1>
				</v-card-title>
				<v-card-text>
					Folgendes Angebot an Kunde senden:<br />
					{{ offer.positions.length }} Positionen<br />
					<strong>Gesamtbetrag {{ getTotal() | formatDigit }}</strong> (exkl. MwSt)<br />
					Liefertermin {{ offer.latestDeliveryDate | formatDate }}<br />
					<br />
					Dieses Angebot ist verbindlich. Der Vorgang kann nicht mehr rückgängig gemacht
					werden.
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn @click="showSubmitDialog = false">Abbrechen</v-btn>
					<v-btn :disabled="loading" class="blexon-button" @click="submitOffer()"
						>Senden</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import httpClient from "../store/httpClient";
import PriceTextBox from "./priceTextBoxComponent.vue";
import OfferPositionComponent from "./offerPositionComponent.vue";

function groupBy(list, keyGetter) {
	const map = new Map();
	list.forEach((item) => {
		const key = keyGetter(item);
		const collection = map.get(key);
		if (!collection) {
			map.set(key, [item]);
		} else {
			collection.push(item);
		}
	});
	return map;
}

export default {
	components: {
		"price-text-box": PriceTextBox,
		"offer-position-component": OfferPositionComponent,
	},
	data() {
		return {
			offer: undefined,
			materials: {},
			worksteps: [],
			loading: false,
			showSubmitDialog: false,
		};
	},
	async mounted() {
		this.loading = true;
		await this.loadMaterials();
		await this.loadOffer();
		await this.loadWorksteps();
		this.loading = false;
	},
	methods: {
		getMaterialGroups() {
			const result = groupBy(this.offer.positions, (p) => p.rawMaterialId);
			return result;
		},

		async submitOffer() {
			try {
				this.loading = true;
				await httpClient().post(
					`api/offer/submitOffer?calculationId=${this.$route.params.calculationId}`
				);
				this.showSubmitDialog = false;
				var fertigerId = this.$route.params.fertigerId;
				this.$router.push({ name: "offers", params: { fertigerId: fertigerId } });
			} catch (err) {
				console.log(err);
			} finally {
				this.loading = false;
			}
		},

		async loadMaterials() {
			try {
				const materials = await httpClient().get(
					`api/Material/GetMaterials?fertigerId=${this.$route.params.fertigerId}&includeInactive=true`
				);
				this.materials = materials.data.flatMap((w) => w.materials);
			} catch (err) {
				console.log(err);
			}
		},

		async loadOffer() {
			try {
				this.loading = true;
				const offer = await httpClient().get(
					`api/Offer/GetOffer?calculationId=${this.$route.params.calculationId}&fertigerId=${this.$route.params.fertigerId}`
				);
				this.offer = offer.data;
			} catch (err) {
				console.log(err);
			}
		},

		async loadWorksteps() {
			try {
				const worksteps = await httpClient().get(
					`api/Offer/GetWorkStepsForCalculationWithoutOrder?calculationId=${this.$route.params.calculationId}`
				);
				this.worksteps = worksteps.data;
			} catch (err) {
				console.log(err);
			}
		},

		getMaterialnameOfFertiger(materialId) {
			const material = this.materials.find((m) => m.blexonId === materialId)
			if (material) {
				return material.fertigerBeschreibung;
			}
			return "";
		},

		getMaterialPrice(material) {
			var price = this.offer.materialPriceOverrides.find((m) => m.materialId === material[0]);
			if (!price) {
				return 0;
			}
			if (price.pricePerKg.setByManufacturer !== null) {
				return price.pricePerKg.setByManufacturer;
			}
			return price.pricePerKg.calculatedByBlexon;
		},

		getTotal() {
			let total = 0;
			for (const position of this.offer.positions) {
				const worksteps = this.worksteps.filter((w) => w.articleId === position.articleId);
				const totalOfPosition = worksteps.reduce(
					(partialSum, a) => partialSum + a.manufacturerPrice,
					0
				);
				total += totalOfPosition * position.quantity;
			}
			return total;
		},

		async updateMaterialPrice(newPrice, materialId) {
			var price = this.offer.materialPriceOverrides.find((m) => m.materialId === materialId);
			price.pricePerKg.setByManufacturer = newPrice;
			try {
				await httpClient().post(
					`api/Offer/UpdateMaterialPriceForOffer?calculationId=${this.$route.params.calculationId}&` +
						`newPrice=${newPrice}&` +
						`materialId=${materialId}`
				);
				await this.loadWorksteps();
			} catch (err) {
				console.log(err);
			} finally {
				this.$emit("loading", false);
			}
		},
	},
};
</script>

<style scoped>
.offerPosition {
	display: flex;
}

.offerPosition > div {
	padding: 1em;
}

.properties {
	width: 200px;
}

.properties > span {
	text-align: right;
	display: inline-block;
	width: 60px;
}

.priceTable {
	display: flex;
	text-align: right;
	margin-bottom: 10px;
}

.priceTable > div {
	min-width: 100px;
	align-self: end;
}
</style>
